class StickyAtc extends HTMLElement {
  connectedCallback() {
    this._mainBtn = document.querySelector('.product-single product-form .js-product-add');
    this._stickyBtn = this.querySelector('.sticky-atc__btn');
    this._mainInput = document.querySelector('.product-single product-form form input[name="id"]');
    this._stickyInput = document.getElementById('sticky-atc-variant-id');
    this._mainQty = document.querySelector('.product-single input[name="quantity"]');
    this._stickyQty = document.getElementById('sticky-atc-quantity');
    this._stickyForm = this.querySelector('form');

    this._setupVisibility();
    this._setupVariantSync();
    this._setupQuantitySync();
    this._setupStateSync();
  }

  _setupVisibility() {
    // Sticky-atc is ALWAYS position: fixed at viewport bottom — we only
    // toggle visibility based on whether an inline rest-position element
    // (the subscribe widget on desktop, the reviews section on mobile) is
    // in viewport. Two separate roles, two separate elements:
    //   - Inline rest-position element: always visible inline. The page's
    //     theme renders this (or a clone of the sticky-atc snippet) below
    //     the subscribe widget on desktop / after reviews on mobile.
    //   - This <sticky-atc> element: floating bar pinned at viewport bottom
    //     when the inline element is OUT of viewport (user hasn't reached
    //     it yet), hidden when the inline element is IN viewport.
    //
    // Because we never toggle position/in-flow state on sticky-atc, no
    // layout shifts can feed back into the visibility check — no flicker.
    const setup = () => {
      // Locate the inline rest-position element. On desktop, this is the
      // subscribe widget's block wrapper (the inline ATC stub sits right
      // after it). On mobile, this is the reviews section. Sticky bar
      // hides when the inline element OR its successor inline-ATC is in
      // viewport.
      const subscribeRaw = document.querySelector('.spectrum-sub')
        || document.querySelector('recharge-subscription-widget')
        || document.querySelector('.recharge-subscription-widget');
      const subscribeAnchor = subscribeRaw
        ? (subscribeRaw.closest('.product-single__box__block')
           || subscribeRaw.closest('.shopify-block')
           || subscribeRaw)
        : null;
      const reviewsSection = document.querySelector(
        '[id*="custom-reviews"], [id$="__spectrum_reviews"], .js-section__custom-reviews, [data-section-type*="reviews"]'
      );
      const inlineAtc = document.querySelector('#pdp-inline-atc');

      // Sticky bar is always position:fixed; just toggle visibility.
      this.classList.remove('is-in-flow');

      // Desktop: align the sticky bar's left/width to the subscribe column
      // (matches live nutritionfaktory — bar doesn't span full viewport on
      // desktop, only the right-column width). Mobile: full viewport width
      // via stylesheet, clear inline left/width.
      const alignToColumn = () => {
        const isMobile = window.matchMedia('(max-width: 767px)').matches;
        if (isMobile) {
          this.style.left = '';
          this.style.right = '';
          this.style.width = '';
          return;
        }
        // Use the inline ATC's bounding box as the column reference (it sits
        // in the column, so its width matches). Falls back to subscribeAnchor.
        const ref = inlineAtc || subscribeAnchor;
        if (!ref) return;
        const r = ref.getBoundingClientRect();
        this.style.left = (r.left + window.scrollX) + 'px';
        this.style.right = 'auto';
        this.style.width = r.width + 'px';
      };

      const showBar = () => {
        alignToColumn();
        this.classList.add('is-fixed', 'is-visible');
        this.setAttribute('aria-hidden', 'false');
        document.body.classList.add('has-fixed-sticky-atc');
      };
      const hideBar = () => {
        this.classList.remove('is-visible');
        this.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('has-fixed-sticky-atc');
        // Keep is-fixed so the bar stays position:fixed even when hidden
        // (avoids any layout flash when re-shown).
        this.classList.add('is-fixed');
      };

      // Re-align on resize / scroll (column position can shift slightly).
      window.addEventListener('resize', alignToColumn, { passive: true });
      window.addEventListener('scroll', alignToColumn, { passive: true });

      // Watch the inline ATC stub when present, otherwise fall back to the
      // anchor element. Inline stub is the most reliable signal because its
      // position is purely determined by content above it, never shifts when
      // the sticky bar's visibility toggles.
      const isMobile = () => window.matchMedia('(max-width: 767px)').matches;
      const pickWatch = () => {
        if (inlineAtc) return inlineAtc;
        return isMobile()
          ? (reviewsSection || subscribeAnchor)
          : (subscribeAnchor || reviewsSection);
      };

      let observer = null;
      const setupObserver = () => {
        const target = pickWatch();
        if (!target) {
          // No anchor at all — fall back to always-visible.
          showBar();
          return;
        }

        // Default to showing the bar at first paint so the user sees a CTA
        // immediately. The IO will hide it on the first real intersection
        // tick once the inline ATC has rendered + the relocate JS has run.
        // (On mobile, the inline ATC starts at display:none via CSS until
        // the relocate IIFE moves it after the reviews section. Without
        // this default, IO sees a zero-rect element at setup and the bar
        // never appears until the user scrolls.)
        showBar();

        if (observer) observer.disconnect();
        observer = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            // Skip zero-rect entries (e.g. element still display:none) —
            // IO will re-fire once the element becomes laid out.
            const r = entry.boundingClientRect;
            if (r.width === 0 && r.height === 0) continue;

            if (entry.isIntersecting) {
              // In viewport — hide sticky, user can see the inline ATC.
              hideBar();
            } else if (r.top > 0) {
              // Below viewport — user hasn't reached the inline ATC yet,
              // show the floating sticky bar so they have a CTA.
              showBar();
            } else {
              // Above viewport — user has already scrolled past the inline
              // ATC. Don't show the sticky bar; they've already seen the CTA.
              hideBar();
            }
          }
        }, { threshold: 0, rootMargin: '0px' });
        observer.observe(target);
      };

      // Re-evaluate when the inline ATC's relocation flag flips (mobile
      // relocate IIFE sets data-mobile-relocated AFTER our IO is set up).
      // This catches the case where IO's first tick saw display:none.
      const watchRelocation = () => {
        const inline = document.querySelector('#pdp-inline-atc');
        if (!inline) return;
        new MutationObserver(() => {
          // Re-pick target and re-observe (display:none → display:block
          // gives IO a fresh layout to react to).
          setupObserver();
        }).observe(inline, { attributes: true, attributeFilter: ['data-mobile-relocated'] });
      };
      watchRelocation();

      setupObserver();
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(setupObserver, 120);
      }, { passive: true });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', setup, { once: true });
    } else {
      requestAnimationFrame(setup);
    }
  }

  _setupVariantSync() {
    if (!this._mainInput || !this._stickyInput) return;
    // Sync on change events from variant pickers
    document.querySelector('.product-single')?.addEventListener('change', () => {
      this._stickyInput.value = this._mainInput.value;
      if (this._mainQty && this._stickyQty) this._stickyQty.value = this._mainQty.value;
    });
    // Fallback poll (only while bar is visible) to catch programmatic updates
    this._syncInterval = setInterval(() => {
      if (this.classList.contains('is-visible') && this._mainInput && this._stickyInput) {
        this._stickyInput.value = this._mainInput.value;
        if (this._mainQty && this._stickyQty) this._stickyQty.value = this._mainQty.value;
      }
    }, 300);
  }

  _setupQuantitySync() {
    if (!this._mainQty || !this._stickyQty) return;
    // Mirror the main PDP quantity input onto the sticky form on every input
    // event (covers +/- buttons, typing, programmatic changes).
    this._mainQty.addEventListener('input', () => {
      this._stickyQty.value = this._mainQty.value;
    });
    this._mainQty.addEventListener('change', () => {
      this._stickyQty.value = this._mainQty.value;
    });
    // Final safety: copy the latest main qty into the sticky form right
    // before submit, so the cart-add request always uses the visible value.
    if (this._stickyForm) {
      this._stickyForm.addEventListener('submit', () => {
        if (this._mainQty && this._stickyQty) {
          this._stickyQty.value = this._mainQty.value || '1';
        }
      }, true);
    }
    // Initialize.
    this._stickyQty.value = this._mainQty.value || '1';
  }

  _setupStateSync() {
    // Mirror .is-adding / .is-added / disabled from main button to sticky.
    if (this._mainBtn && this._stickyBtn) {
      this._stateObserver = new MutationObserver(() => {
        this._stickyBtn.classList.toggle('is-adding', this._mainBtn.classList.contains('is-adding'));
        this._stickyBtn.classList.toggle('is-added', this._mainBtn.classList.contains('is-added'));
        this._stickyBtn.disabled = this._mainBtn.disabled;
      });
      this._stateObserver.observe(this._mainBtn, { attributes: true, attributeFilter: ['class', 'disabled'] });
    }

    // Variant availability — toggles which sticky button (Add to cart
    // vs Notify Me) is visible. Drive this off the actual variant data
    // (the `variants:change` event fired by variant-selects) instead of
    // the main button's `disabled` attribute, because that attribute
    // can be wrong/unchanged when Liquid initial-renders a different
    // variant (selected_or_first_available_variant fallback) than the
    // one in the URL — in that case the disabled never flips and the
    // bar stays stuck.
    const variantSelector = document.querySelector('.product-single variant-selects, .product-single variant-radios');
    const updateAvailability = (variant) => {
      if (!variant) return;
      const oos = !variant.available;
      this.classList.toggle('sticky-atc--oos', oos);
      this.classList.toggle('sticky-atc--available', !oos);
      // Keep the Notify Me anchor's variant id in sync.
      const notifyBtn = this.querySelector('.js-product-notify');
      if (notifyBtn) notifyBtn.setAttribute('data-variant-id', String(variant.id));
      // Sync the sticky form's hidden variant id so submitting / Notify
      // Me uses the right SKU.
      if (this._stickyInput) this._stickyInput.value = String(variant.id);
    };
    if (variantSelector) {
      variantSelector.addEventListener('variants:change', (e) => {
        if (e.detail && e.detail.variant) updateAvailability(e.detail.variant);
      });
      // Initial state: derive from the variant-selects' currentVariant
      // (set by its connectedCallback) — falls back to URL ?variant=
      // matching against its variant data when needed.
      const init = () => {
        const cv = variantSelector.currentVariant
          || (() => {
            const idFromUrl = new URLSearchParams(window.location.search).get('variant');
            if (!idFromUrl || typeof variantSelector.getVariantData !== 'function') return null;
            return variantSelector.getVariantData().find(v => String(v.id) === idFromUrl) || null;
          })();
        if (cv) updateAvailability(cv);
      };
      // variant-selects sets currentVariant in connectedCallback; wait
      // a tick to make sure that has run.
      requestAnimationFrame(init);
    }
  }

  disconnectedCallback() {
    this._visibilityObserver?.disconnect();
    this._stateObserver?.disconnect();
    if (this._restScroll) {
      window.removeEventListener('scroll', this._restScroll);
      window.removeEventListener('resize', this._restScroll);
    }
    clearInterval(this._syncInterval);
  }
}

customElements.define('sticky-atc', StickyAtc);
